import React, { useEffect, useState } from 'react'
import { client } from '../../client/client'
import { Psychologist } from '../../types'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { DataGrid, GridColDef } from '@material-ui/data-grid'

interface AverageHours {
  id: string
  psychologistId: number
  psychologistName: string
  totalDays: number
  totalDuration: number
  avgDurationPerDay: number
}

const AverageHoursPage: React.FC = () => {
  const [stats, setStats] = useState<AverageHours[]>([])
  const [loading, setLoading] = useState(false)
  const [psychologists, setPsychologists] = useState<Psychologist[]>([])
  const [selectedPsychologist, setSelectedPsychologist] = useState<number>()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,
    total: 0
  })

  useEffect(() => {
    const fetchPsychologists = async () => {
      const data = await client.getPsychologistOptions()
      setPsychologists(data)
    }
    fetchPsychologists()
  }, [])

  useEffect(() => {
    fetchStats()
  }, [
    selectedPsychologist,
    startDate,
    endDate,
    pagination.page,
    pagination.pageSize
  ])

  const fetchStats = async () => {
    setLoading(true)
    try {
      const response = await client.getAvgRegisteredActivities(
        selectedPsychologist,
        startDate,
        endDate,
        pagination.page,
        pagination.pageSize
      )
      const formattedData = response.data.map((item: any, index: number) => ({
        id: index.toString(),
        psychologistId: item.psychologistId,
        psychologistName: item.psychologistName,
        totalDays: item.totalDays,
        totalDuration: item.totalDuration,
        avgDurationPerDay: item.avgDurationPerDay
      }))
      setStats(formattedData)
      setPagination((prev) => ({ ...prev, total: response.total }))
    } catch (error) {
      console.error('Error fetching average hours:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSelectedPsychologist(undefined)
    setStartDate('')
    setEndDate('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const columns: GridColDef[] = [
    {
      field: 'psychologistName',
      headerName: 'Psychologist',
      flex: 1,
      sortable: false
    },
    {
      field: 'totalDays',
      headerName: 'Total Days',
      flex: 0.5,
      sortable: false
    },
    {
      field: 'totalDuration',
      headerName: 'Total Duration (minutes)',
      flex: 0.5,
      sortable: false
    },
    {
      field: 'avgDurationPerDay',
      headerName: 'Average Duration Per Day (minutes)',
      flex: 0.5,
      sortable: false
    }
  ]

  return (
    <Box
      p={3}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        paddingTop: '5rem'
      }}
    >
      <Typography variant="h4" gutterBottom>
        Average Hours in Selected Time Range
      </Typography>
      <Grid container spacing={3} style={{ marginBottom: 20 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Psychologist</InputLabel>
            <Select
              value={selectedPsychologist || ''}
              onChange={(e) =>
                setSelectedPsychologist(e.target.value as number)
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Psychologists</em>
              </MenuItem>
              {psychologists.map((psychologist) => (
                <MenuItem key={psychologist.id} value={psychologist.id}>
                  {`${psychologist.user.firstName} ${psychologist.user.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="From"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="To"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClearFilters}
            style={{ height: '100%', width: '100%' }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
      <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={stats}
          columns={columns}
          pageSize={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          paginationMode="server"
          rowCount={pagination.total}
          onPageChange={(newPage) =>
            setPagination((prev) => ({ ...prev, page: newPage + 1 }))
          }
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({ ...prev, pageSize: newPageSize }))
          }
          loading={loading}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          autoHeight
        />
      </div>
    </Box>
  )
}

export default AverageHoursPage
